import { FolderOpen, FolderX, Loader2, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FreeCutLogo } from '@/components/brand/freecut-logo'

type Status =
  | { kind: 'initializing' }
  | { kind: 'unavailable' }
  | { kind: 'pick' }
  | { kind: 'reconnect'; handleName: string }

interface Props {
  status: Status
  onPickFolder: () => void
  onReconnect: () => void
  busyLabel?: string | null
}

export function WorkspaceGateSplash({ status, onPickFolder, onReconnect, busyLabel }: Props) {
  const isBusy = Boolean(busyLabel)
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center">
        <FreeCutLogo variant="full" size="lg" className="justify-center mb-8" />

        {status.kind === 'initializing' && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> 正在加载工作区…
          </div>
        )}

        {status.kind === 'unavailable' && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-destructive">
              <FolderX className="h-5 w-5" />
              <span className="font-medium">当前浏览器暂不支持</span>
            </div>
            <p className="text-sm text-muted-foreground">
              快剪依赖 Chromium 内核浏览器提供的文件系统访问能力。建议使用 Chrome、Edge、Brave 或 Arc；Firefox 和 Safari 目前还不支持完整工作区流程。
            </p>
          </div>
        )}

        {status.kind === 'pick' && (
          <div className="space-y-5">
            <div>
              <h1 className="text-2xl font-semibold mb-2">选择你的工作区文件夹</h1>
              <p className="text-sm text-muted-foreground">
                项目、素材和缓存都会以普通文件的形式保存在你自己控制的文件夹里。选择一个本地目录后，后续项目都会保存在这里。
              </p>
            </div>
            <Button size="lg" className="gap-2" onClick={onPickFolder}>
              {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
              {busyLabel ?? '选择文件夹'}
            </Button>
            <p className="text-xs text-muted-foreground">
              提示：如果你希望多设备同步，可以把工作区放在 Dropbox、iCloud 或 Google Drive 之类的同步目录中，后面也可以再切换。
            </p>
          </div>
        )}

        {status.kind === 'reconnect' && (
          <div className="space-y-5">
            <div>
              <h1 className="text-2xl font-semibold mb-2">重新连接工作区</h1>
              <p className="text-sm text-muted-foreground">
                需要重新获取对 <span className="font-mono">{status.handleName}</span> 的访问权限。浏览器会在部分会话之间自动要求重新授权，这是正常现象。
              </p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="lg" className="gap-2" onClick={onPickFolder}>
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderOpen className="h-4 w-4" />}
                {busyLabel ?? '换一个文件夹'}
              </Button>
              <Button size="lg" className="gap-2" onClick={onReconnect} disabled={isBusy}>
                <RefreshCw className="h-4 w-4" />
                重新连接
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
